/**
 * Roles in the platform.
 *
 * Renamed from the legacy `ADMIN` → `CEO` (per Aqib 2026-08-01).
 * `GENERAL_MANAGER` was promoted to `GM` (still distinct from `MANAGER`).
 * `TEAM_LEAD` and `STAFF` retained with TitleCase labels for display.
 *
 * Hierarchy is by numeric rank — higher rank = more privilege.
 * `CLIENT` has rank 0 — separate auth scope, no staff privileges.
 */

export const USER_ROLES = ['CEO', 'GM', 'Manager', 'TeamLead', 'Staff', 'CLIENT'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const ROLE_RANK: Record<UserRole, number> = {
  CEO: 100,
  GM: 90,
  Manager: 80,
  TeamLead: 60,
  Staff: 35,
  CLIENT: 0,
};

export const ROLE_LABELS: Record<UserRole, string> = {
  CEO: 'CEO',
  GM: 'General Manager',
  Manager: 'Manager',
  TeamLead: 'Team Lead',
  Staff: 'Staff',
  CLIENT: 'Client',
};

/** Internal roles only — excludes CLIENT (clients have a separate auth scope). */
export const STAFF_ROLES = ['CEO', 'GM', 'Manager', 'TeamLead', 'Staff'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/** Privileged roles — can manage users, settings, destructive ops. */
export const PRIVILEGED_ROLES: readonly StaffRole[] = ['CEO', 'GM'] as const;

/** Roles that can create/edit engagements. */
export const ENGAGEMENT_AUTHOR_ROLES: readonly StaffRole[] = ['CEO', 'GM', 'Manager'] as const;

/** Roles that can bulk-delete items / clients. */
export const BULK_DELETE_ROLES: readonly StaffRole[] = ['CEO', 'GM'] as const;

export function isStaffRole(role: UserRole): role is StaffRole {
  return (STAFF_ROLES as readonly string[]).includes(role);
}

export function isPrivileged(role: UserRole): boolean {
  return role === 'CEO' || role === 'GM';
}

export function roleAtLeast(actual: UserRole, required: StaffRole): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}